/**
 * 추가공간 기록 — v2 문서 버전.
 *
 * 방 선택과 같은 모양이다: 카드를 고르면 그 공간의 짐 기록이 열리고, 끝나면 목록으로
 * 돌아온다. 프로덕션도 같은 구조를 두 어댑터에 나눠 갖고 있다
 * (`AdditionalSpaceFlowAdapter` / `RoomObservingFlowAdapter`).
 *
 * 다른 점은 목록의 출처뿐이다 — 방은 스케치 세션에서, 추가공간은 별도의 옵저빙 세션
 * 목록에서 온다. 그래서 데이터 이름만 갈라 두고 화면 구성은 같은 규칙을 따른다.
 */
import type { AssetDocument, DataSource } from "@miso/design-system";

import { STAGE_DOCUMENT_IDS } from "./counselFullFlow";

export const ADDITIONAL_SPACE_REFS = {
  /** 추가공간 세션 목록. 호스트가 상담에서 만든다. */
  entries: "counsel.additionalSpaceEntries",
  sections: "additionalSpaces.sections",
  doneInstances: "additionalSpaces.doneInstances",
} as const;

export const ADDITIONAL_SPACE_DATA: DataSource[] = [
  { id: "entries", source: ADDITIONAL_SPACE_REFS.entries },
  {
    id: "sections",
    derive: ADDITIONAL_SPACE_REFS.sections,
    args: { entries: { $var: "entries" } },
  },
  {
    id: "additionalSpaceToRecord",
    derive: ADDITIONAL_SPACE_REFS.doneInstances,
    args: { entries: { $var: "entries" } },
  },
];

const doc: AssetDocument = {
  id: STAGE_DOCUMENT_IDS.additionalSpaces,
  schemaVersion: 2,
  initial: "pick",
  data: ADDITIONAL_SPACE_DATA,
  steps: {
    pick: {
      kind: "screen",
      layout: {
        type: "Screen",
        props: {
          variant: "question",
          title: "기록할 추가공간을 선택해주세요.",
        },
        slots: {
          footer: [
            {
              type: "CtaButton",
              props: { label: "완료" },
              on: {
                onPress: [
                  {
                    do: "callAction",
                    name: "completeAdditionalSpaceObserving",
                  },
                  { do: "submit" },
                ],
              },
            },
          ],
        },
        children: [
          {
            type: "ItemPicker",
            bind: "additionalSpaceToRecord",
            props: {
              sections: { $var: "sections" },
              mode: "navigation",
              multiple: false,
              requireAllItems: true,
              reviewTitle: "기록할 추가공간",
              reviewButtonLabel: "완료",
            },
            on: {
              onItemPress: [
                {
                  do: "setValues",
                  values: {
                    targetSessionId: { $var: "$event.id" },
                    roomLabel: { $var: "$event.title" },
                    roomType: { $var: "$event.roomType" },
                  },
                },
                { do: "goto", step: "record" },
              ],
            },
          },
        ],
      },
      next: { kind: "exit" },
    },

    record: {
      kind: "transform",
      code: "return {};",
      outputKey: "_record",
      next: { kind: "chain", target: STAGE_DOCUMENT_IDS.movingItems },
    },
  },
};

export const ADDITIONAL_SPACES = { doc };
